import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  getPaginationRowModel,
  getSortedRowModel,
  ColumnFiltersState,
  SortingState,
  getFilteredRowModel,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DataTablePagination } from "./DataTablePagination";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  headerColor?: string;
  alignment?: "left" | "center" | "right";
  rowsPerPage?: number;
}
import SearchFilter from "./SearchFilter"; // Ensure correct import path
import { useEffect, useState } from "react";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  headerColor?: string;
  alignment?: "left" | "center" | "right";
  rowsPerPage?: number;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  headerColor = "bg-gray-200", // default header color
  alignment = "left", // default alignment
  rowsPerPage = 5,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState<string>("");

  const table = useReactTable({
    data,
    columns,
    pageCount: Math.ceil(data.length / rowsPerPage),
    initialState: { pagination: { pageSize: rowsPerPage } },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  // Set the filter for vehicle number
  useEffect(() => {
    table.getColumn("vehicle_number")?.setFilterValue(searchValue);
  }, [searchValue, table]);

  const alignmentClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  return (
    <div>
      <SearchFilter
        placeholder="Search by vehicle number..."
        value={searchValue}
        onChange={setSearchValue}
        hasFilters={false} // Change to true if you have filter functionality
      />
      <div className="rounded-md border mt-4">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id} className={headerColor}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    className={`${alignmentClasses[alignment]} ${headerColor} w-24`}
                    style={{ wordBreak: "break-word" }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={alignmentClasses[alignment]}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <DataTablePagination table={table} />
      </div>
    </div>
  );
}
